/**
 * 额外菜单配置
 * 用于动态注入不在后端返回菜单中的固定页面
 *
 * NOTE: 这些菜单项通过数组索引位置注入到后端返回的菜单结构中，
 * 如果后端菜单结构发生变更，需要同步调整这里的 index 和 subIndex
 */

/** 账户信息菜单项（注入到 menuList[2].menuList） */
export const ACCOUNT_INFO_MENU_ITEM: any = {
    path: '/user/info',
    componentName: 'accountInfo',
    redirect: '',
    groupType: null,
    level: null,
    code: null,
    num: null,
    meta: {
        title: '账户信息',
        icon: '',
        noCache: true,
        hiddenFlag: true,
        indexFlag: 0,
        functionVOList: []
    },
    children: [],
    menuId: 1013,
    sortNo: 2
};

/** 产品发布菜单项（注入到 menuList[3].menuList[0].children） */
export const PRODUCT_PUBLISH_MENU_ITEM: any = {
    path: '/admin/product/publish',
    componentName: 'productPublish',
    redirect: '',
    groupType: null,
    level: null,
    code: null,
    num: null,
    meta: {
        title: '产品发布',
        icon: '',
        noCache: true,
        hiddenFlag: true,
        indexFlag: 0,
        functionVOList: []
    },
    children: [],
    menuId: 1014,
    sortNo: 3
};

/** 物料管理菜单项（注入为新的“库存”顶级分组） */
export const INVENTORY_PRODUCT_MENU_ITEM: any = {
    path: '/admin/inventory/product',
    componentName: 'inventoryProduct',
    redirect: '',
    groupType: null,
    level: null,
    code: null,
    num: null,
    meta: {
        title: '物料管理',
        icon: '',
        noCache: true,
        hiddenFlag: false,
        indexFlag: 0,
        functionVOList: []
    },
    children: [],
    menuId: 1015,
    sortNo: 1
};

/** 库存顶级菜单分组 */
export const INVENTORY_MENU_GROUP = {
    groupVO: {
        id: 9999,
        name: '库存',
        icon: 'Grid'
    },
    menuList: [INVENTORY_PRODUCT_MENU_ITEM]
};

/**
 * 续费管理菜单项（可见兜底：后端未下发菜单时注入到语义匹配分组，通常与「应用列表」同组）。
 *
 * 后端菜单/功能点由 sql/saas_renewal_management_permission.sql 创建
 * （type=1 菜单 url=renewalCenter、path=/admin/renewal/index，挂 1067「应用管理」下，
 * type=2 端点 /chsRenewal/getBusinessRenewalList）；菜单未执行脚本时，本占位项保证
 * 「续费管理」入口可用（去重守卫为递归扫描，后端下发后只清理占位项）。
 *
 * 页面路径 /admin/renewal/index 对应 router/map.ts 的 renewalCenter。
 */
export const RENEWAL_CENTER_MENU_ITEM: any = {
    path: '/admin/renewal/index',
    componentName: 'renewalCenter',
    redirect: '',
    groupType: null,
    level: null,
    code: null,
    num: null,
    meta: {
        title: '续费管理',
        icon: '',
        noCache: true,
        hiddenFlag: false,
        indexFlag: 0,
        functionVOList: []
    },
    children: [],
    menuId: 1019,
    sortNo: 2
};

/**
 * 消息中心菜单项（兜底隐藏路由：2026-10-02 起后端已下发可见菜单 1461「消息中心」
 * （url=messageCenter，挂载于「应用 → 应用管理(1067)」children 下，path 与本项一致）。
 *
 * 2026-10-05：`injectExtraMenus` 的去重守卫已**递归扫描全部层级**（componentName 或 path 命中
 * 即视为已存在），后端下发后只清理历史占位项、不再注入；后端缺失时占位项也只会落到
 * 语义匹配的分组（不会进「首页」组）。
 *
 * 页面路径 /admin/message/index 对应 router/map.ts 的 messageCenter；
 * 通知中心数据来自 /chsMessage/*.json（赋权见 sql/message_center_menu_enable.sql，
 * 5 个消息功能点 1434~1438 已迁挂菜单 1461 下）。
 */
export const MESSAGE_CENTER_MENU_ITEM: any = {
    path: '/admin/message/index',
    componentName: 'messageCenter',
    redirect: '',
    groupType: null,
    level: null,
    code: null,
    num: null,
    meta: {
        title: '消息中心',
        icon: '',
        noCache: true,
        hiddenFlag: true,
        indexFlag: 0,
        functionVOList: []
    },
    children: [],
    menuId: 1017,
    sortNo: 9
};

/**
 * 审批中心菜单项（隐藏路由：侧边菜单不展示，由 HeaderBar 铃铛入口进入）
 *
 * 页面路径 /admin/approval/index 对应 router/map.ts 的 approvalCenter；
 * 数据来自 /wf/*.json（需按 api-code/xqyy_dao/src/main/resources/sql/wf_engine_permission.sql 赋权）。
 */
export const APPROVAL_CENTER_MENU_ITEM: any = {
    path: '/admin/approval/index',
    componentName: 'approvalCenter',
    redirect: '',
    groupType: null,
    level: null,
    code: null,
    num: null,
    meta: {
        title: '审批中心',
        icon: '',
        noCache: true,
        hiddenFlag: true,
        indexFlag: 0,
        functionVOList: []
    },
    children: [],
    menuId: 1018,
    sortNo: 10
};

/**
 * 注入额外菜单到后端返回的菜单列表
 * @param menuList 后端返回的原始菜单列表
 * @returns 注入后的菜单列表
 */
// ======================== 隐藏路由占位注入工具（2026-10-05 递归化） ========================

/** 扁平化全部分组的菜单树（含各级 children），用于跨分组 / 跨层级的重复检测 */
function flattenMenusDeep(menuList: any[]): any[] {
    const out: any[] = [];
    const walk = (list: any[] | undefined) => {
        (list || []).forEach((menu) => {
            out.push(menu);
            if (menu?.children?.length) walk(menu.children);
        });
    };
    (menuList || []).forEach((group) => walk(group?.menuList));
    return out;
}

/** 按引用从菜单树任意层级移除某个菜单项，返回是否移除成功 */
function removeMenuDeep(menuList: any[], target: any): boolean {
    const walk = (list: any[] | undefined): boolean => {
        if (!Array.isArray(list)) return false;
        const index = list.indexOf(target);
        if (index !== -1) {
            list.splice(index, 1);
            return true;
        }
        return list.some((menu) => walk(menu?.children));
    };
    return (menuList || []).some((group) => walk(group?.menuList));
}

/**
 * 兜底分组选择：隐藏路由占位项应注入到哪个分组
 *
 * 为什么不能再用「第一个非空分组」（2026-10-05 缺陷根因）：
 *   第一个非空分组固定是「首页」组，而 `MenuSide#findPathIndex` 按**分组顺序**取第一个
 *   命中该 path 的分组 → 占位项一旦落在首页组，隐藏路由（消息中心 / 审批中心）的左侧
 *   分组就永远被判成「首页」。现按语义优先：
 *   1) 含兄弟功能（appmgt / workflow / wfDef）的分组；
 *   2) 分组名含「应用 / 系统 / 设置」的分组；
 *   3) 最后一个非空分组（末尾兜底，绝不再默认「首页」组）。
 */
function pickFallbackGroup(menuList: any[], siblingComponentNames: string[] = []): any | undefined {
    const groups = (menuList || []).filter((g: any) => Array.isArray(g?.menuList) && g.menuList.length > 0);
    const bySibling = groups.find((g: any) =>
        flattenMenusDeep([g]).some((m: any) => siblingComponentNames.includes(m?.componentName))
    );
    if (bySibling) return bySibling;
    const byName = groups.find((g: any) => /应用|系统|设置/.test(String(g?.groupVO?.name || '')));
    if (byName) return byName;
    return groups[groups.length - 1];
}

/**
 * 确保「隐藏路由」菜单项存在于菜单树中（幂等 + 全层级去重 + 清理历史脏数据）
 *
 * 守卫必须**递归扫描全部层级**：后端 2026-10-02 起已把「消息中心」下发改挂到
 * 「应用 → 应用管理(1067) → 消息中心(1461)」的 children 下，旧守卫只扫一级
 * `group.menuList` → 判定「不存在」→ 把占位项注入首页组（点消息中心左侧变「首页」）。
 *
 * 三种情形：
 *   1) 后端已下发（同 componentName 或同 path 的非占位项存在）→ 移除占位项
 *      （含旧版本已写进 localStorage 的首页组脏数据）后返回；
 *   2) 只有存量占位项 → 保底保留，若它被旧逻辑放进了非目标分组则迁移到目标分组；
 *   3) 完全不存在（后端未下发）→ 注入到语义匹配的兜底分组。
 *
 * @param menuList 后端菜单树（就地修改）
 * @param item 占位隐藏菜单项
 * @param siblingComponentNames 同功能兄弟组件名（用于兜底分组的语义匹配）
 */
function ensureHiddenRouteMenu(menuList: any[], item: any, siblingComponentNames: string[] = []): void {
    const sameRoute = flattenMenusDeep(menuList).filter(
        (m: any) => m?.componentName === item.componentName || m?.path === item.path
    );

    const backendItem = sameRoute.find((m: any) => m?.menuId !== item.menuId);
    if (backendItem) {
        sameRoute
            .filter((m: any) => m?.menuId === item.menuId)
            .forEach((placeholder: any) => removeMenuDeep(menuList, placeholder));
        return;
    }

    const injected = sameRoute.find((m: any) => m?.menuId === item.menuId);
    const targetGroup = pickFallbackGroup(menuList, siblingComponentNames);

    if (injected) {
        const currentGroup = (menuList || []).find((g: any) => (g?.menuList || []).includes(injected));
        if (targetGroup && currentGroup && targetGroup !== currentGroup) {
            const index = currentGroup.menuList.indexOf(injected);
            if (index !== -1) currentGroup.menuList.splice(index, 1);
            targetGroup.menuList.push(injected);
        }
        return;
    }

    if (targetGroup) {
        targetGroup.menuList.push(item);
    }
}

export function injectExtraMenus(menuList: any[]): any[] {
    // 防御性编程：确保索引位置存在再注入；避免重复 push，同时更新缓存数据的 hiddenFlag
    if (menuList[2]?.menuList) {
        const accountExists = menuList[2].menuList.some((m: any) => m.menuId === ACCOUNT_INFO_MENU_ITEM.menuId);
        if (!accountExists) {
            menuList[2].menuList.push(ACCOUNT_INFO_MENU_ITEM);
        }
    }
    if (menuList[3]?.menuList?.[0]?.children) {
        const children = menuList[3].menuList[0].children;
        const publishExists = children.some((m: any) => m.menuId === PRODUCT_PUBLISH_MENU_ITEM.menuId);
        if (!publishExists) {
            children.push(PRODUCT_PUBLISH_MENU_ITEM);
        } else {
            // 更新已有菜单项的 hiddenFlag（兼容 localStorage 缓存的旧数据）
            const existing = children.find((m: any) => m.menuId === PRODUCT_PUBLISH_MENU_ITEM.menuId);
            if (existing) {
                existing.meta.hiddenFlag = PRODUCT_PUBLISH_MENU_ITEM.meta.hiddenFlag;
            }
        }
    }

    // 注入独立的“库存”顶级菜单分组（仅当后端未配置时才注入，避免重复显示）
    const hasInventoryGroup = menuList.some(
        (group: any) =>
            group.groupVO?.name === INVENTORY_MENU_GROUP.groupVO.name ||
            group.groupVO?.id === INVENTORY_MENU_GROUP.groupVO.id ||
            group.menuList?.some(
                (m: any) =>
                    m.componentName === INVENTORY_PRODUCT_MENU_ITEM.componentName ||
                    m.menuId === INVENTORY_PRODUCT_MENU_ITEM.menuId
            )
    );
    if (!hasInventoryGroup) {
        menuList.push(INVENTORY_MENU_GROUP as any);
    }

    // 注入「消息中心」隐藏路由（HeaderBar 铃铛入口）：
    // 后端 2026-10-02 起已下发可见菜单 1461（挂在「应用管理」children 下）→ 递归守卫命中后
    // 只做「清理历史占位项」，不再往首页组推占位项
    ensureHiddenRouteMenu(menuList, MESSAGE_CENTER_MENU_ITEM, ['appmgt']);

    // 注入「审批中心」隐藏路由（HeaderBar 铃铛入口，侧边菜单不展示；后端不下发该路由，
    // 工作流的可见菜单是 1439「审批中心」/workflow 与 1460「流程定义」/wfDef）
    ensureHiddenRouteMenu(menuList, APPROVAL_CENTER_MENU_ITEM, ['appmgt', 'workflow', 'wfDef']);

    // 注入「续费管理」（可见菜单兜底）：后端菜单见 sql/saas_renewal_management_permission.sql，
    // 未下发时落到「应用列表」同组（语义匹配 sibling），保证续费入口可用
    ensureHiddenRouteMenu(menuList, RENEWAL_CENTER_MENU_ITEM, ['appmgt', 'businessPlatformList']);

    return menuList;
}
